import React, { useEffect, useMemo, useState } from 'react';
import { Newspaper, Save, Plus, Trash2, RotateCcw, Loader2, ExternalLink } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

const ACCENT_OPTIONS = [
  { key: 'cmic',  label: 'Azul CMIC' },
  { key: 'teal',  label: 'Verde Teal' },
  { key: 'amber', label: 'Ámbar' },
];

export default function AdminFlashPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  async function load() {
    setLoading(true); setError(''); setOk('');
    try {
      const res = await api.get('/flash/bulletins');
      setConfig(res.data || { sections: [] });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const totalIssues = useMemo(() => (
    (config?.sections || []).reduce((acc, s) => acc + (s.issues?.length || 0), 0)
  ), [config]);

  async function save() {
    setSaving(true); setError(''); setOk('');
    try {
      await api.put('/admin/flash/bulletins', { sections: config.sections || [] });
      setOk('Guardado correctamente.');
      setTimeout(() => setOk(''), 3500);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al guardar');
    } finally { setSaving(false); }
  }
  async function resetDefault() {
    if (!window.confirm('¿Restaurar las 3 secciones por defecto? Se descartarán los cambios actuales.')) return;
    setSaving(true);
    try { await api.post('/admin/flash/reset'); await load(); setOk('Restaurado.'); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error'); }
    finally { setSaving(false); }
  }

  function updateSection(si, patch) {
    const sections = [...config.sections];
    sections[si] = { ...sections[si], ...patch };
    setConfig({ ...config, sections });
  }
  function updateSectionLabel(si, loc, value) {
    updateSection(si, { label: { ...(config.sections[si].label || {}), [loc]: value } });
  }
  function updateSectionDescription(si, loc, value) {
    updateSection(si, { description: { ...(config.sections[si].description || {}), [loc]: value } });
  }
  function updateIssue(si, ii, field, value) {
    const sections = [...config.sections];
    const issues = [...(sections[si].issues || [])];
    issues[ii] = { ...issues[ii], [field]: value };
    sections[si] = { ...sections[si], issues };
    setConfig({ ...config, sections });
  }
  function addIssue(si) {
    const sections = [...config.sections];
    const issues = [...(sections[si].issues || []), { title: 'Nueva edición', date: '', file: '/periodicals/', description: '' }];
    sections[si] = { ...sections[si], issues };
    setConfig({ ...config, sections });
  }
  function removeIssue(si, ii) {
    if (!window.confirm('¿Eliminar esta edición?')) return;
    const sections = [...config.sections];
    sections[si] = { ...sections[si], issues: sections[si].issues.filter((_, i) => i !== ii) };
    setConfig({ ...config, sections });
  }
  function addSection() {
    const id = window.prompt('ID único para la nueva sección (ej. "2027"):', '');
    if (!id) return;
    const sections = [...(config.sections || []), {
      id, label: { es: id, en: id }, accent: 'cmic',
      description: { es: '', en: '' }, issues: [],
    }];
    setConfig({ ...config, sections });
  }
  function removeSection(si) {
    if (!window.confirm(`¿Eliminar la sección "${config.sections[si]?.label?.es}" y todas sus ediciones?`)) return;
    setConfig({ ...config, sections: config.sections.filter((_, i) => i !== si) });
  }

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>;
  if (!config) return <div className="p-6 text-charcoal-500">Sin datos.</div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-flash-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <Newspaper size={22} className="text-cmic-600" /> Boletín Flash
          </h1>
          <p className="text-xs text-charcoal-500 mt-0.5">
            {config.sections?.length || 0} secciones · {totalIssues} ediciones
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href="/es/flash" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-charcoal-600 hover:bg-gray-100 rounded-lg"><ExternalLink size={14} /> Vista previa</a>
          <button onClick={resetDefault} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 rounded-lg border border-amber-200"><RotateCcw size={14} /> Restaurar</button>
          <button onClick={save} disabled={saving} data-testid="flash-save" className="inline-flex items-center gap-2 px-4 py-2 bg-cmic-600 text-white font-medium rounded-lg text-sm hover:bg-cmic-700 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
          </button>
        </div>
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {ok && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg p-3 text-sm">{ok}</div>}

      {(config.sections || []).map((section, si) => (
        <div key={si} className="bg-white rounded-xl border border-gray-200" data-testid={`flash-section-${section.id}`}>
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
            <span className="font-semibold text-charcoal-700 text-sm">Sección: <code className="bg-white px-1.5 py-0.5 rounded text-xs">{section.id}</code></span>
            <button onClick={() => removeSection(si)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Eliminar sección"><Trash2 size={14} /></button>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Etiqueta (ES)"><input value={section.label?.es || ''} onChange={(e) => updateSectionLabel(si, 'es', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Label (EN)"><input value={section.label?.en || ''} onChange={(e) => updateSectionLabel(si, 'en', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Color"><select value={section.accent || 'cmic'} onChange={(e) => updateSection(si, { accent: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">{ACCENT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}</select></Field>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Descripción (ES)"><textarea rows={2} value={section.description?.es || ''} onChange={(e) => updateSectionDescription(si, 'es', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Description (EN)"><textarea rows={2} value={section.description?.en || ''} onChange={(e) => updateSectionDescription(si, 'en', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></Field>
            </div>
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <p className="text-xs font-semibold text-charcoal-500 uppercase tracking-wider">Ediciones</p>
              {(section.issues || []).map((issue, ii) => (
                <div key={ii} className="border border-gray-100 rounded-lg p-3 grid md:grid-cols-12 gap-2 bg-gray-50/30">
                  <input value={issue.title} onChange={(e) => updateIssue(si, ii, 'title', e.target.value)} placeholder="Título" className="md:col-span-3 border border-gray-200 rounded px-2 py-1.5 text-sm" />
                  <input value={issue.date || ''} onChange={(e) => updateIssue(si, ii, 'date', e.target.value)} placeholder="YYYY-MM" className="md:col-span-2 border border-gray-200 rounded px-2 py-1.5 text-sm font-mono" />
                  <input value={issue.file} onChange={(e) => updateIssue(si, ii, 'file', e.target.value)} placeholder="/periodicals/archivo.pdf" className="md:col-span-4 border border-gray-200 rounded px-2 py-1.5 text-xs font-mono" />
                  <input value={issue.description || ''} onChange={(e) => updateIssue(si, ii, 'description', e.target.value)} placeholder="Descripción opcional" className="md:col-span-2 border border-gray-200 rounded px-2 py-1.5 text-xs" />
                  <button onClick={() => removeIssue(si, ii)} className="md:col-span-1 p-1.5 text-red-600 hover:bg-red-50 rounded" title="Eliminar"><Trash2 size={13} /></button>
                </div>
              ))}
              <button onClick={() => addIssue(si)} className="inline-flex items-center gap-1.5 text-cmic-600 hover:bg-cmic-50 rounded-lg px-3 py-1.5 text-sm"><Plus size={13} /> Agregar edición</button>
            </div>
          </div>
        </div>
      ))}
      <button onClick={addSection} className="inline-flex items-center gap-1.5 px-4 py-2.5 border-2 border-dashed border-gray-300 hover:border-cmic-400 hover:text-cmic-700 text-charcoal-500 rounded-lg text-sm font-medium w-full justify-center"><Plus size={14} /> Agregar sección</button>
    </div>
  );
}

function Field({ label, children }) {
  return (<label className="block"><span className="block text-xs font-medium text-charcoal-600 mb-1">{label}</span>{children}</label>);
}
